import { DIMMED_ANNOUNCEMENT_OPACITY } from '@/features/hazards';
import { GeoJsonProperties } from 'geojson';
import { Expression } from 'mapbox-gl';

/**
 * Identifies the announcement that is currently selected.
 *
 * An announcement is a single hazard — one tile in the list. Selecting one isolates that hazard's own
 * map objects, of which there can be several (e.g. one polygon per municipality a warning covers).
 * Other announcements are dimmed even when they belong to the same event.
 *
 * Kept as an object rather than a bare id so that matching on more than the hazard id stays a change
 * to this file alone.
 */
export type AnnouncementKey = {
  hazardId: string;
};

/**
 * Whether a map feature belongs to the selected announcement.
 * When nothing is selected, every feature is considered part of the "selection" (i.e. nothing is dimmed).
 */
export const isInSelectedAnnouncement = (properties: GeoJsonProperties, key: AnnouncementKey | null): boolean => {
  if (!key) return true;

  return properties?.id === key.hazardId;
};

/**
 * Wraps an opacity paint value so features outside of the selected announcement are dimmed.
 *
 * The base value is multiplied rather than replaced, so per-feature styling (e.g. a polygon with
 * its own `fill-opacity` property) is preserved relative to the rest of the map.
 *
 * @param base - The opacity paint value used when nothing is selected.
 * @param key - The selected announcement, or `null` when there is no selection.
 */
export const buildAnnouncementOpacity = (base: number | Expression, key: AnnouncementKey | null): number | Expression => {
  if (!key) return base;

  const isSelected: Expression = ['==', ['get', 'id'], key.hazardId];

  return ['case', isSelected, base, ['*', base, DIMMED_ANNOUNCEMENT_OPACITY]];
};
